import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PreviewTemplate from 'components/common/previewTemplate';
import TemplateStatusTag, { isTemplateSelectable } from 'components/templates/templateStatus';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import CreateTemplateForm from 'pages/Client/CreateTemplateForm';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { CampaignData } from 'services/api/adminCampaigns.api';
import { useGetBusinessUnitQuery, useGetBusinessUnitTemplatesQuery } from 'services/api/businessUnits.api';
import { getTemplateAttributesForBusinessType } from 'services/types/client';
import type { TemplateResponse } from 'services/types/message';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

/**
 * Ordered body parameter descriptors for a template. Each entry is the param name for a named
 * template ({{modelo}}) or null for a positional one ({{1}}). Prefers Meta's `example` metadata;
 * falls back to parsing {{...}} placeholders from the body text when no example is present —
 * otherwise the mapping UI is hidden and the campaign submits empty/unnamed bodyComponents
 * (Meta errors 132000 - param count mismatch, or 100 - parameter name missing).
 */
const getTemplateBodyParamNames = (template: TemplateResponse[number] | null): (string | null)[] => {
  if (!template) return [];
  const body = template.components.find((c) => c.type === 'BODY');
  if (!body) return [];
  const namedParams = body.example?.body_text_named_params;
  if (namedParams?.length) return namedParams.map((p) => p.param_name);
  const tokens = [...(body.text ?? '').matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)].map((m) => m[1]);
  const uniqueTokens = [...new Set(tokens)];
  if (uniqueTokens.length === 0) return [];
  const isNamedFormat = template.parameter_format === 'named' || uniqueTokens.some((t) => !/^\d+$/.test(t));
  return uniqueTokens.map((t) => (isNamedFormat ? t : null));
};

/**
 * Maps a named param to a template attribute value based on common parameter names. Returns null
 * when there is no confident match — callers must only accept selectable attributes, never the raw
 * WhatsApp param value. For after-sales business units a brand/model param refers to the vehicle the
 * client already owns, not the one they are shopping for.
 */
const getClientAttributeForParam = (paramName: string, businessUnitType?: string | null): string | null => {
  const lowerParamName = paramName.toLowerCase();
  const isAfterSales = businessUnitType === 'after_sales';

  if (['client', 'cliente', 'nombre', 'nombre_cliente', 'name'].includes(lowerParamName)) {
    return 'client.firstName';
  }
  if (isAfterSales && ['vin', 'chasis', 'numero_chasis', 'serie'].includes(lowerParamName)) {
    return null;
  }
  if (isAfterSales && ['ano', 'anio', 'year', 'ano_auto'].includes(lowerParamName)) {
    return 'ownedCar.year';
  }
  if (isAfterSales && ['patente', 'placa', 'matricula'].includes(lowerParamName)) {
    return 'ownedCar.licensePlate||tu vehículo';
  }
  if (['brand', 'marca', 'brand_of_interest'].includes(lowerParamName)) {
    return isAfterSales ? 'ownedCar.brand||tu vehículo' : 'client.brandOfInterest';
  }
  if (['model', 'modelo', 'auto', 'car', 'model_of_interest'].includes(lowerParamName)) {
    return isAfterSales ? 'ownedCar.model||tu vehículo' : 'client.modelsOfInterest';
  }
  if (['branch_office', 'sucursal', 'branch'].includes(lowerParamName)) {
    return 'client.branchOffice';
  }
  if (['email', 'correo'].includes(lowerParamName)) {
    return 'client.email';
  }
  if (['phone', 'telefono', 'celular'].includes(lowerParamName)) {
    return 'client.phone';
  }

  return null;
};

const getPrefilledParamsForTemplate = (
  template: TemplateResponse[number] | null,
  attributeOptions: { title: string; value: string }[],
  businessUnitType?: string | null
): { title: string; value: string }[] | null => {
  if (!template) return null;
  const bodyComponent = template.components.find((c) => c.type === 'BODY');
  const namedParams = bodyComponent?.example?.body_text_named_params;

  if (!namedParams?.length) return null;

  const matched = namedParams.map((param) => {
    const autoValue = getClientAttributeForParam(param.param_name, businessUnitType);
    return autoValue ? (attributeOptions.find((attr) => attr.value === autoValue) ?? null) : null;
  });
  // Only prefill when every param maps to a selectable attribute; otherwise leave it for the user
  // to pick, so we never inject a value that is not in the selectable list (and keep alignment).
  if (matched.some((attr) => attr === null)) return null;
  return matched as { title: string; value: string }[];
};

interface SelectBusinessUnitAndTemplateProps {
  campaignData: CampaignData;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onNext: () => void;
}

const SelectBusinessUnitAndTemplate: React.FC<SelectBusinessUnitAndTemplateProps> = ({
  campaignData,
  setCampaignData,
  onNext,
}) => {
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const [openNewTemplate, setOpenNewTemplate] = useState(false);
  const [templateBodyParameters, setTemplateBodyParameters] = useState<{ title: string; value: string }[]>([]);

  useEffect(() => {
    const buId = selectedBusinessUnitId && selectedBusinessUnitId > 0 ? selectedBusinessUnitId : null;
    if (buId !== (campaignData.businessUnitId ?? null)) {
      setCampaignData((prev) => ({
        ...prev,
        businessUnitId: buId,
        selectedTemplate: null,
        templateBodyParameters: [],
      }));
      setTemplateBodyParameters([]);
    }
  }, [selectedBusinessUnitId, setCampaignData, campaignData.businessUnitId]);

  const { data: templates = [] } = useGetBusinessUnitTemplatesQuery(campaignData.businessUnitId || 0, {
    skip: !campaignData.businessUnitId,
  });
  const { data: businessUnitData } = useGetBusinessUnitQuery(campaignData.businessUnitId ?? 0, {
    skip: !campaignData.businessUnitId,
  });
  const attributeOptions = getTemplateAttributesForBusinessType(businessUnitData?.type);

  // Ordered param names (or null for positional). Drives both the count gate and how each mapped
  // value is stored, so named templates ({{modelo}}) carry their parameter_name to Meta.
  const bodyParamNames = getTemplateBodyParamNames(campaignData.selectedTemplate ?? null);
  const bodyComponentsLength = bodyParamNames.length;

  // Preview substitution: put each selected attribute where its placeholder is. For named templates
  // ({{modelo}}) emit { param_name, example } so the named placeholder is replaced; for positional
  // ({{1}}) emit the ordered values. Falls back to the template's own examples when nothing is mapped.
  const previewBodyComponent = campaignData.selectedTemplate?.components.find((c) => c.type === 'BODY');
  const previewBodyVariableExamples = templateBodyParameters.length
    ? bodyParamNames.some((name) => name !== null)
      ? templateBodyParameters.map((p, index) => ({ param_name: bodyParamNames[index] ?? '', example: `{${p.title}}` }))
      : templateBodyParameters.map((p) => `{${p.title}}`)
    : (previewBodyComponent?.example?.body_text?.[0] ?? previewBodyComponent?.example?.body_text_named_params ?? null);

  const handleTemplateBodyParametersChange = (parameters: { title: string; value: string }[]) => {
    if (!bodyComponentsLength || parameters.length <= bodyComponentsLength) {
      setTemplateBodyParameters(parameters);

      const bodyComponents = parameters.map((p, index) => {
        const paramName = bodyParamNames[index];
        return paramName ? { paramName, value: p.value } : p.value;
      });

      setCampaignData((prev) => ({
        ...prev,
        templateBodyParameters: bodyComponents,
      }));
    }
  };

  const handleTemplateSelect = (templateName: string) => {
    const targetTemplate = templates?.find((template) => template.name === templateName);
    if (targetTemplate) {
      const prefilledParams = getPrefilledParamsForTemplate(targetTemplate, attributeOptions, businessUnitData?.type);
      const templateNamedParams = targetTemplate.components.find((c) => c.type === 'BODY')?.example
        ?.body_text_named_params;

      if (prefilledParams && templateNamedParams) {
        setTemplateBodyParameters(prefilledParams);
        setCampaignData((prev) => ({
          ...prev,
          selectedTemplate: targetTemplate,
          templateBodyParameters: prefilledParams.map((p, index) => ({
            paramName: templateNamedParams[index]?.param_name || `param_${index}`,
            value: p.value,
          })),
        }));
      } else {
        setTemplateBodyParameters([]);
        setCampaignData((prev) => ({
          ...prev,
          selectedTemplate: targetTemplate,
          templateBodyParameters: [],
        }));
      }
    }
  };

  const canProceed =
    campaignData.businessUnitId &&
    campaignData.selectedTemplate &&
    (!bodyComponentsLength || templateBodyParameters.length === bodyComponentsLength);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 860, mx: 'auto' }}>
      {/* No BU selected */}
      {!campaignData.businessUnitId && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.6 }}>
            Selecciona una unidad de negocio en la barra lateral para comenzar.
          </Typography>
        </Box>
      )}

      {/* Template selector */}
      {campaignData.businessUnitId && (
        <Box>
          <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, mb: 0.5 }}>
            Plantilla de WhatsApp
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.55, mb: 2.5 }}>
            Elige la plantilla y configura sus parámetros dinámicos.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Autocomplete
              fullWidth
              options={templates ?? []}
              getOptionLabel={(option) => option.name}
              getOptionDisabled={(option) => !isTemplateSelectable(option.status)}
              value={campaignData.selectedTemplate ?? null}
              onChange={(_, template) => {
                if (template) handleTemplateSelect(template.name);
                else {
                  setTemplateBodyParameters([]);
                  setCampaignData((prev) => ({ ...prev, selectedTemplate: null, templateBodyParameters: [] }));
                }
              }}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}
                >
                  <span>{option.name}</span>
                  <TemplateStatusTag status={option.status} />
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar plantilla"
                  placeholder="Escribe para filtrar..."
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: SURFACE_CONTAINER_LOWEST,
                      borderRadius: RADIUS_ITEM,
                      fontFamily: 'Jost, sans-serif',
                      '& fieldset': { border: 'none' },
                      boxShadow: SHADOW,
                    },
                    '& label': { fontFamily: 'Jost, sans-serif' },
                  }}
                />
              )}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: GLASS_BACKGROUND,
                    backdropFilter: GLASS_BLUR,
                    borderRadius: RADIUS_ITEM,
                    boxShadow: SHADOW,
                    '& .MuiMenuItem-root, & .MuiAutocomplete-option': {
                      fontFamily: 'Jost, sans-serif',
                      color: NAVY,
                    },
                  },
                },
              }}
            />
            <Button
              onClick={() => setOpenNewTemplate(true)}
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: NAVY,
                bgcolor: 'rgba(21, 24, 58, 0.07)',
                borderRadius: RADIUS_COIN,
                px: 2.5,
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.12)' },
              }}
            >
              Crear nueva
            </Button>
          </Box>
        </Box>
      )}

      {/* Template create modal */}
      {openNewTemplate && (
        <Modal open onClose={() => setOpenNewTemplate(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', md: '70%' },
              maxHeight: '90vh',
              overflow: 'auto',
              bgcolor: GLASS_BACKGROUND,
              backdropFilter: GLASS_BLUR,
              borderRadius: RADIUS_CARD,
              boxShadow: '0px 24px 64px rgba(21, 24, 58, 0.14)',
              p: 4,
            }}
          >
            <CreateTemplateForm businessUnitId={campaignData.businessUnitId as number} />
          </Box>
        </Modal>
      )}

      {/* Template body params */}
      {!!campaignData.selectedTemplate && !!bodyComponentsLength && (
        <Box>
          <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, mb: 0.5 }}>
            Parámetros de la Plantilla
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.55, mb: 2.5 }}>
            Selecciona los atributos del cliente para cada variable del cuerpo del mensaje.
          </Typography>
          <Autocomplete
            multiple
            id="template-components"
            value={templateBodyParameters}
            onChange={(_, value) => handleTemplateBodyParametersChange(value)}
            getOptionLabel={(option) => option.title}
            options={attributeOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Seleccionar atributos"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: SURFACE_CONTAINER_LOWEST,
                    borderRadius: RADIUS_ITEM,
                    fontFamily: 'Jost, sans-serif',
                    '& fieldset': { border: 'none' },
                    boxShadow: SHADOW,
                  },
                }}
              />
            )}
          />
          {bodyComponentsLength && templateBodyParameters.length !== bodyComponentsLength && (
            <Typography variant="body2" sx={{ mt: 1, fontFamily: 'Jost, sans-serif', color: '#B00020' }}>
              * Esta plantilla requiere exactamente {bodyComponentsLength} parámetros
            </Typography>
          )}
        </Box>
      )}

      {/* Preview */}
      {campaignData.selectedTemplate && (
        <Box>
          <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, mb: 2 }}>
            Vista Previa
          </Typography>
          <PreviewTemplate
            headerEnabled={campaignData.selectedTemplate.components.some((c) => c.type === 'HEADER')}
            headerText={campaignData.selectedTemplate.components.find((c) => c.type === 'HEADER')?.text || null}
            headerFormat={campaignData.selectedTemplate.components.find((c) => c.type === 'HEADER')?.format || null}
            headerFileUrl={
              campaignData.selectedTemplate.components.find((c) => c.type === 'HEADER' && c.format === 'IMAGE')?.example
                ?.header_handle?.[0] || null
            }
            bodyText={campaignData.selectedTemplate.components.find((c) => c.type === 'BODY')?.text || ''}
            bodyVariableExamples={previewBodyVariableExamples}
            footerEnabled={campaignData.selectedTemplate.components.some((c) => c.type === 'FOOTER')}
            footerText={campaignData.selectedTemplate.components.find((c) => c.type === 'FOOTER')?.text || null}
            buttons={campaignData.selectedTemplate.components.find((c) => c.type === 'BUTTONS')?.buttons || null}
          />
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!canProceed}
          sx={{
            bgcolor: YELLOW,
            color: NAVY,
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            borderRadius: RADIUS_COIN,
            px: 4,
            boxShadow: SHADOW,
            '&:hover': { bgcolor: '#e6c009' },
            '&:disabled': { bgcolor: 'rgba(21,24,58,0.1)', color: 'rgba(21,24,58,0.3)' },
          }}
        >
          Siguiente
        </Button>
      </Box>
    </Box>
  );
};

export default SelectBusinessUnitAndTemplate;
